/**
 * Room Store
 *
 * Zustand store for room-related UI state.
 * Manages current room code, countdown, errors, and game start state.
 */

import { create } from 'zustand';
import type { PlayerPresence, RoomGameType } from 'shared-types';

interface RoomState {
  // Current room code (null when not in a room)
  currentRoomCode: string | null;
  // Room creator ID (null when not in a room)
  creatorId: string | null;
  // Room game type ('free' or 'paid')
  gameType: RoomGameType | null;
  // Whether this is a matchmaking room (vs private room)
  isMatchmaking: boolean;
  // Players in the room (sorted by join order)
  players: PlayerPresence[];
  // Countdown seconds remaining (null when not counting down)
  countdown: number | null;
  // Whether game is starting
  isStarting: boolean;
  // Error message for room operations
  error: string | null;

  // Actions
  setRoomCode: (code: string | null) => void;
  setCreatorId: (creatorId: string | null) => void;
  setGameType: (gameType: RoomGameType | null) => void;
  setIsMatchmaking: (isMatchmaking: boolean) => void;
  setPlayers: (players: PlayerPresence[]) => void;
  setCountdown: (seconds: number | null) => void;
  setIsStarting: (isStarting: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const initialState = {
  currentRoomCode: null,
  creatorId: null,
  gameType: null,
  isMatchmaking: false,
  players: [],
  countdown: null,
  isStarting: false,
  error: null
};

export const useRoomStore = create<RoomState>((set) => ({
  ...initialState,

  setRoomCode: (code) => {
    set({ currentRoomCode: code });
  },

  setCreatorId: (creatorId) => {
    set({ creatorId });
  },

  setGameType: (gameType) => {
    set({ gameType });
  },

  setIsMatchmaking: (isMatchmaking) => {
    set({ isMatchmaking });
  },

  setPlayers: (players) => {
    set({ players });
  },

  setCountdown: (seconds) => {
    set({ countdown: seconds });
  },

  setIsStarting: (isStarting) => {
    set({ isStarting });
  },

  setError: (error) => {
    set({ error });
  },

  reset: () => {
    set(initialState);
  }
}));
